import { useState } from 'react'

import { StadiumPreviewMockup } from '@/features/marketing/mockups/StadiumPreviewMockup'

/**
 * Stadium demo visual — looping WebM/MP4 of a real Stadium run with
 * a static mockup fallback for when the video file isn't there yet.
 *
 * Why a video and not just the static still?
 *   Static screenshots can't sell motion: the bracket-flash, the
 *   crossfade between phases, the spark-burst on the podium reveal.
 *   A 15–30s loop is what makes a clan owner go "oh, that's the
 *   thing on the venue TV".
 *
 * Anti-scraping posture:
 *   The video is intentionally light protection — watermarked
 *   subtly ("e404 Stadium · demo loop") so a competitor lifting
 *   it has to either burn the watermark off or replace the asset.
 *   The real moat lives in the back-end (polling, payouts, bracket
 *   generation, the squadron context) — the public visual is the
 *   pitch, not the secret. The full interactive demo at
 *   /stadium/demo is open (no gate) — same content for a crawler
 *   as for a person.
 *
 * File layout:
 *   /public/videos/stadium-demo.webm   (preferred — smaller, modern)
 *   /public/videos/stadium-demo.mp4    (fallback for Safari pre-15)
 *   When neither is present, the <video> element fires onError and we
 *   fall back to <StadiumPreviewMockup> — keeps the page working
 *   during the period between this code shipping and the asset
 *   landing in /public.
 *
 * Variant note: `full` is used by the /stadium/demo page; it strips
 * the watermark down and lets the video fill its container.
 */
export function StadiumDemoVisual({
  variant = 'inline',
}: {
  variant?: 'inline' | 'full'
}) {
  // Track whether the video failed to load. We start optimistic
  // (assume the asset exists); if the browser fires `onError` on
  // either source the element collapses and we render the static
  // mockup instead. No flicker because the static is rendered behind
  // the video at all times (z-stacked) — the video sits on top when
  // it loads, and reveals the static when it doesn't.
  const [videoFailed, setVideoFailed] = useState(false)

  return (
    <div className="relative aspect-video w-full overflow-hidden bg-[#06060f]">
      {/* Static fallback always rendered behind the video. When the
          video element loads, it sits on top of this and hides it. */}
      <div className="absolute inset-0">
        <StadiumPreviewMockup />
      </div>

      {!videoFailed && (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          onError={() => setVideoFailed(true)}
          aria-label="Tournament Stadium demo loop"
        >
          {/* WebM first — smaller file, modern browsers prefer it.
              MP4 fallback covers older Safari. If neither resolves
              the element fires onError. */}
          <source src="/videos/stadium-demo.webm" type="video/webm" />
          <source src="/videos/stadium-demo.mp4" type="video/mp4" />
        </video>
      )}

      {/* Watermark — subtle, bottom-right, small enough not to compete
          with the broadcast composition but visible in any screen-
          shot or recording lifted from the page. */}
      <div
        className="absolute right-2 bottom-2 z-10 select-none"
        aria-hidden
      >
        <p
          className="font-display text-[9px] font-extrabold tracking-[0.28em] uppercase"
          style={{
            color: 'rgba(255,255,255,0.45)',
            textShadow: '0 0 8px rgba(0,0,0,0.85)',
            letterSpacing: variant === 'full' ? '0.22em' : '0.28em',
            fontSize: variant === 'full' ? '11px' : '9px',
          }}
        >
          e404 stadium · demo
        </p>
      </div>
    </div>
  )
}
